import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

// Shared look for Input / Textarea / Select so every form field matches.
// Fields use `border-control` (≥3:1, the edge of something clickable) — never
// the decorative `border-subtle`. See /design-system/GUIDE.md.
export const fieldClassName = cn(
	"w-full rounded-xl border border-border-control bg-bg-surface px-3 py-2 text-sm text-text-primary",
	"placeholder:text-text-tertiary",
	"transition-colors duration-150",
	"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg-surface",
	"disabled:cursor-not-allowed disabled:bg-action-disabled-bg disabled:text-action-disabled-fg",
);

export const fieldErrorClassName =
	"border-danger focus-visible:ring-danger";

export function fieldId(id: string | undefined, label: string | undefined) {
	return id || label?.toLowerCase().replace(/\s+/g, "-");
}

interface FieldShellProps {
	id?: string;
	label?: string;
	error?: string;
	children: ReactNode;
}

// Label + error wrapper. Renders the bare control when neither is set,
// so a field drops into flex rows without an extra wrapper div.
export function FieldShell({ id, label, error, children }: FieldShellProps) {
	if (!label && !error) return <>{children}</>;

	return (
		<div className="space-y-1.5">
			{label && (
				<label
					htmlFor={id}
					className="block text-sm font-medium text-text-primary"
				>
					{label}
				</label>
			)}
			{children}
			{error && <p className="text-xs text-danger">{error}</p>}
		</div>
	);
}
